import { useState, type FormEvent } from 'react'
import { CORRECTION_KINDS, type CorrectionKind } from '@wf/shared'
import { ApiError } from '../api/client'
import { localYmd } from '../orders/workshop'
import { CORRECTION_KIND_LABEL } from './labels'
import { useCreateCorrection } from './useCorrections'

/**
 * Formular „Zeit-Korrektur anfragen" der Meine-Liste: der Mitarbeiter meldet dem Chef einen vergessenen
 * oder falschen Stempel. Wie im Alt-Monolithen ist bei jeder Art außer „Sonstiges" die Uhrzeit Pflicht,
 * bei „Sonstiges" stattdessen eine Freitext-Beschreibung.
 */
export function CorrectionRequestForm({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const today = localYmd(new Date())
  const [kind, setKind] = useState<CorrectionKind>('kommen_vergessen')
  const [day, setDay] = useState(today)
  const [time, setTime] = useState('')
  const [message, setMessage] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const create = useCreateCorrection()

  const sonstiges = kind === 'sonstiges'

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!sonstiges && !time) {
      setLocalError('Bitte die Uhrzeit angeben')
      return
    }
    if (sonstiges && !message.trim()) {
      setLocalError('Bitte kurz beschreiben')
      return
    }
    setLocalError(null)
    create.mutate(
      { kind, corr_day: day, corr_time: time || null, message: message.trim() || undefined },
      { onSuccess: onSent },
    )
  }

  const serverError = create.error instanceof ApiError ? create.error.message : create.error ? 'Senden fehlgeschlagen.' : null

  return (
    <form className="wf-corrform" onSubmit={submit}>
      <div className="wf-chips" role="group" aria-label="Art der Korrektur">
        {CORRECTION_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={kind === k ? 'wf-chip wf-chip--active' : 'wf-chip'}
            aria-pressed={kind === k}
            onClick={() => setKind(k)}
          >
            {CORRECTION_KIND_LABEL[k]}
          </button>
        ))}
      </div>
      <div className="two">
        <label className="wf-field">
          <span className="wf-label">Tag</span>
          <input className="wf-input" type="date" max={today} value={day} onChange={(event) => setDay(event.target.value)} />
        </label>
        <label className="wf-field">
          <span className="wf-label">{sonstiges ? 'Uhrzeit (optional)' : 'Uhrzeit'}</span>
          <input className="wf-input" type="time" value={time} onChange={(event) => setTime(event.target.value)} />
        </label>
      </div>
      <label className="wf-field">
        <span className="wf-label">{sonstiges ? 'Beschreibung' : 'Notiz (optional)'}</span>
        <input
          className="wf-input"
          type="text"
          placeholder="z. B. Kommen vergessen zu stempeln"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
      </label>
      {localError ? (
        <p className="error" role="alert">
          {localError}
        </p>
      ) : null}
      {serverError ? (
        <p className="error" role="alert">
          {serverError}
        </p>
      ) : null}
      <div className="wf-listfoot">
        <button type="button" className="wf-btn wf-btn--ghost" onClick={onClose}>
          Abbrechen
        </button>
        <button type="submit" className="wf-btn wf-btn--primary" disabled={create.isPending}>
          Anfrage senden
        </button>
      </div>
    </form>
  )
}
